import { useCallback, useEffect, useRef, useState } from "react";

interface QueueData<T> {
  data: T | null;
  isLoading: boolean;
  hasFailed: boolean;
}

interface QueueState<T> extends QueueData<T> {
  /** Whose data this is: the scope the request was made for. */
  scopeKey: string;
}

const emptyState = (scopeKey: string): QueueState<never> => ({
  scopeKey,
  data: null,
  isLoading: true,
  hasFailed: false,
});

// The state as it stands for this scope: the same state, or an empty one when it belongs to another scope.
const inScope = <T>(state: QueueState<T>, scopeKey: string) =>
  state.scopeKey === scopeKey ? state : emptyState(scopeKey);

interface QueueDataOptions<T> {
  /**
   * Names the request within the scope (filters, page, a refresh counter). When it changes the
   * page loads again and the last answer stays on screen meanwhile.
   */
  requestKey?: string;
  /**
   * Whose data this is: the company, plus the person where the page is per person. When it
   * changes the old answer is dropped at once and the page starts empty, so another company's
   * data is never shown (or kept after a failed request) under the new one.
   */
  scopeKey: string;
  /** Hears about an answer once, when it arrives. */
  onAnswer?: (data: T) => void;
}

/**
 * Loads what a page lists. Within one scope the last answer stays in `data` while the next
 * one loads or fails, so a hiccup never blanks a list the person is working from; a new
 * scope starts empty. `reload` asks again. Only the answer to the latest request is used.
 */
export function useQueueData<T>(load: () => Promise<T>, { requestKey = "", scopeKey, onAnswer }: QueueDataOptions<T>) {
  const [state, setState] = useState<QueueState<T>>(emptyState(scopeKey));
  const latestRequest = useRef(0);
  const loadRef = useRef(load);
  loadRef.current = load;
  const onAnswerRef = useRef(onAnswer);
  onAnswerRef.current = onAnswer;

  const reload = useCallback(async () => {
    latestRequest.current += 1;
    const thisRequest = latestRequest.current;
    setState((current) => ({ ...inScope(current, scopeKey), isLoading: true, hasFailed: false }));
    try {
      const data = await loadRef.current();
      if (thisRequest === latestRequest.current) {
        setState({ scopeKey, data, isLoading: false, hasFailed: false });
        onAnswerRef.current?.(data);
      }
    } catch (error) {
      if (thisRequest === latestRequest.current) {
        setState((current) => ({ ...inScope(current, scopeKey), isLoading: false, hasFailed: true }));
      }
    }
  }, [scopeKey]);

  useEffect(() => {
    reload();
  }, [reload, requestKey]);

  // Already in the first render after a scope change nothing of the old scope is shown.
  const { data, isLoading, hasFailed } = inScope(state, scopeKey);
  return { data, isLoading, hasFailed, reload };
}
